import React from 'react';
import { Button, Card, Table } from 'react-bootstrap';

import { ProjectCostDemandRate } from '../../../../types';
import { useTypedProjectCostContext } from '../../useTypedProjectCostContext';
import TableControls from '../TableControls';
import { formatCurrencyFromCents } from '../../utils';

const formatDateBR = (iso?: string | null): string => {
  if (!iso) return '—';
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (dateOnly) {
    return `${dateOnly[3]}/${dateOnly[2]}/${dateOnly[1]}`;
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('pt-BR');
};

const formatVigencia = (start?: string | null, end?: string | null): string => {
  if (!start && !end) return 'Sem vigência';
  if (start && end) return `${formatDateBR(start)} — ${formatDateBR(end)}`;
  if (start) return `A partir de ${formatDateBR(start)}`;
  return `Até ${formatDateBR(end)}`;
};

const DemandRatesTable: React.FC = () => {
  const {
    getPagedDemandRates,
    handleEditDemandRate,
    handleDelete,
    saving,
  } = useTypedProjectCostContext<{
    getPagedDemandRates: () => {
      pageItems: ProjectCostDemandRate[];
      totalItems: number;
      totalPages: number;
      currentPage: number;
    };
    handleEditDemandRate: (item: ProjectCostDemandRate) => void;
    handleDelete: (path: string, message: string) => Promise<void>;
    saving: boolean;
  }>();

  const paged = getPagedDemandRates();

  return (
    <Card>
      <Card.Header>Taxas OS / AS</Card.Header>
      <Card.Body className="table-responsive">
        <TableControls
          tableKey="demandRates"
          placeholder="Buscar por tipo, rótulo ou valor..."
          totalItems={paged.totalItems}
          totalPages={paged.totalPages}
          currentPage={paged.currentPage}
        />
        <Table size="sm" hover>
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Rótulo</th>
              <th>Taxa</th>
              <th>Vigência</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {paged.pageItems.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center text-muted py-3">
                  Nenhum registro encontrado.
                </td>
              </tr>
            ) : (
              paged.pageItems.map((item) => (
                <tr key={item.id}>
                  <td>{item.demand_type}</td>
                  <td>{item.label}</td>
                  <td>{formatCurrencyFromCents(item.hourly_rate)}</td>
                  <td>{formatVigencia(item.effective_start, item.effective_end)}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleEditDemandRate(item)}
                      disabled={saving}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => void handleDelete(
                        `/api/projects/costs/demand-rates/${item.id}`,
                        `Excluir taxa ${item.demand_type}?`,
                      )}
                      disabled={saving}
                    >
                      Excluir
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

export default DemandRatesTable;
